import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { Save, History, Upload, ArrowLeft, Bot } from "lucide-react";
import { toast } from "sonner";
import DmnEditor from "../../components/dmn/DmnEditor";
import type { DmnEditorRef } from "../../components/dmn/DmnEditor";
import AiChatSidebar from "../components/studio/AiChatSidebar";
import {
  getDecisions,
  deployDecision,
  type Decision,
} from "../../services/decisionService";

const DecisionDesignerPage = () => {
  const { decisionSlug } = useParams<{ decisionSlug: string }>();
  const navigate = useNavigate();
  const editorRef = useRef<DmnEditorRef>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false);
  const [isAiDrawerVisible, setIsAiDrawerVisible] = useState(false);
  const [currentDmnXml, setCurrentDmnXml] = useState<string>("");

  useEffect(() => {
    const loadDecision = async () => {
      if (!decisionSlug) {
        setLoading(false);
        return;
      }

      try {
        const response = await getDecisions(0, 100);
        const found = response.content.find((d) => d.nameSlug === decisionSlug);
        if (found) {
          setDecision(found);
        } else {
          toast.error("Decision not found");
          navigate("/studio/decisions");
        }
      } catch (error) {
        console.error("Error loading decision:", error);
        toast.error("Failed to load decision");
      } finally {
        setLoading(false);
      }
    };

    loadDecision();
  }, [decisionSlug, navigate]);

  const handleSave = async () => {
    if (!editorRef.current) return;

    setIsSaving(true);
    try {
      await editorRef.current.save();
    } catch (error) {
      console.error("Error saving decision:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!editorRef.current || !decision) return;

    setIsDeploying(true);
    try {
      const xml = await editorRef.current.getXml();
      if (!xml) {
        toast.error("Failed to get DMN XML");
        return;
      }

      await deployDecision(decision.nameSlug || "", xml, decision.name);
      toast.success("Decision deployed successfully");
    } catch (error) {
      console.error("Error deploying decision:", error);
      toast.error("Failed to deploy decision");
    } finally {
      setIsDeploying(false);
    }
  };

  const handleBack = () => {
    navigate("/studio/decisions");
  };

  const handleOpenAiDrawer = async () => {
    if (!editorRef.current) return;
    
    try {
      const xml = await editorRef.current.getXml();
      setCurrentDmnXml(xml || "");
      setIsAiDrawerOpen(true);
      setTimeout(() => setIsAiDrawerVisible(true), 50);
    } catch (error) {
      console.error("Error getting DMN XML for AI:", error);
      toast.error("Failed to open AI assistant");
    }
  };

  const handleCloseAiDrawer = () => {
    setIsAiDrawerVisible(false);
    setTimeout(() => setIsAiDrawerOpen(false), 300);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading decision...</p>
        </div>
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Decision not found</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Decisions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Back to Decisions"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{decision.name}</h1>
            <p className="text-xs text-gray-500">Version {decision.version || "0.1"}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={18} />
            {isSaving ? "Saving..." : "Save"}
          </button>

          <button
            onClick={handleDeploy}
            disabled={isDeploying}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload size={18} />
            {isDeploying ? "Deploying..." : "Deploy"}
          </button>

          <button
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            title="Version History"
          >
            <History size={18} />
            History
          </button>

          <button
            onClick={handleOpenAiDrawer}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            title="AI Assistant"
          >
            <Bot size={18} />
            AI Assistant
          </button>
        </div>
      </div>

      {/* DMN Editor */}
      <div className="flex-1 overflow-hidden">
        <DmnEditor ref={editorRef} decision={decision} onSave={setDecision} />
      </div>

      {/* AI Assistant - Portal render */}
      {isAiDrawerOpen &&
        createPortal(
          <AiChatSidebar
            isOpen={isAiDrawerVisible}
            onClose={handleCloseAiDrawer}
            bpmnXml={currentDmnXml}
            onBpmnModified={async (newXml: string) => {
              if (editorRef.current) {
                await editorRef.current.importXml(newXml);
                setCurrentDmnXml(newXml);
              }
            }}
          />,
          document.body
        )}
    </div>
  );
};

export default DecisionDesignerPage;
